import {DetailedError, parseResponse} from 'hono/client';
import {err, ok} from 'framework';
import type {Page, Result} from 'framework';
import type {RpcClient} from '@/infrastructure/rpc/rpc-client';
import type {
  CreateSavedMealInput,
  ListRecentLineIngredientsInput,
  ListSavedMealsInput,
  LookupLineIngredientByBarcodeInput,
  SavedMealRepository,
  SearchLineIngredientsInput,
  UpdateSavedMealInput,
} from '../../application/ports/saved-meal.repository';
import type {LineIngredientCandidate} from '../../domain/line-ingredient-candidate';
import type {SavedMealError} from '../../domain/errors';
import {
  InvalidSavedMealInputError,
  NetworkError,
  SavedMealNotFoundError,
  ServerError,
  UnauthorizedError,
} from '../../domain/errors';
import type {RecentLineIngredient} from '../../domain/recent-line-ingredient';
import type {SavedMeal, SavedMealSummary} from '../../domain/saved-meal';
import type {SavedMealId} from '../../domain/saved-meal-id.value-object';
import type {LineCandidateByBarcodeDto, LineCandidatesDto} from '../dto/saved-meal.dto';
import type {LineCandidateDtoToCandidateMapper} from '../mappers/line-candidate-dto-to-candidate.mapper';
import type {RecentItemDtoToRecentLineIngredientMapper} from '../mappers/recent-item-dto-to-recent-line-ingredient.mapper';
import type {SavedMealDtoToSavedMealMapper} from '../mappers/saved-meal-dto-to-saved-meal.mapper';
import type {SavedMealSummaryDtoToSummaryMapper} from '../mappers/saved-meal-summary-dto-to-summary.mapper';

export class RpcSavedMealRepository implements SavedMealRepository {
  constructor(
    private readonly rpcClient: RpcClient,
    private readonly mealMapper: SavedMealDtoToSavedMealMapper,
    private readonly summaryMapper: SavedMealSummaryDtoToSummaryMapper,
    private readonly candidateMapper: LineCandidateDtoToCandidateMapper,
    private readonly recentItemMapper: RecentItemDtoToRecentLineIngredientMapper,
  ) {}

  async list(input: ListSavedMealsInput): Promise<Result<Page<SavedMealSummary>, SavedMealError>> {
    try {
      const dto = await parseResponse(
        this.rpcClient['saved-meals'].$get({
          query: {
            ...(input.cursor !== null && {cursor: input.cursor}),
            ...(input.q.length > 0 && {q: input.q}),
            ...(input.limit !== undefined && {limit: String(input.limit)}),
          },
        }),
      );
      if ('error' in dto || dto.meta === undefined) {
        return err(new ServerError());
      }
      return ok({
        items: dto.data.map(item => this.summaryMapper.transform(item)),
        nextCursor: dto.meta.nextCursor,
        hasMore: dto.meta.hasMore,
      });
    } catch (cause) {
      return this.mapError(cause);
    }
  }

  async getById(id: SavedMealId): Promise<Result<SavedMeal, SavedMealError>> {
    try {
      const dto = await parseResponse(
        this.rpcClient['saved-meals'][':savedMealId'].$get({
          param: {savedMealId: id},
        }),
      );
      return ok(this.mealMapper.transform(dto));
    } catch (cause) {
      return this.mapError(cause);
    }
  }

  async create(input: CreateSavedMealInput): Promise<Result<SavedMeal, SavedMealError>> {
    try {
      const dto = await parseResponse(
        this.rpcClient['saved-meals'].$post({
          json: {
            name: input.name,
            description: input.description,
            lines: input.lines.map(line => ({
              displayName: line.displayName,
              quantityGrams: line.quantityGrams,
              kcal: line.kcal,
              proteinGrams: line.proteinGrams,
              carbsGrams: line.carbsGrams,
              fatGrams: line.fatGrams,
              origin: line.origin,
            })),
          },
        }),
      );
      return ok(this.mealMapper.transform(dto));
    } catch (cause) {
      return this.mapError(cause);
    }
  }

  async update(input: UpdateSavedMealInput): Promise<Result<SavedMeal, SavedMealError>> {
    try {
      const dto = await parseResponse(
        this.rpcClient['saved-meals'][':savedMealId'].$patch({
          param: {savedMealId: input.savedMealId},
          json: {
            name: input.name,
            description: input.description,
            lines: input.lines.map(line => ({
              displayName: line.displayName,
              quantityGrams: line.quantityGrams,
              kcal: line.kcal,
              proteinGrams: line.proteinGrams,
              carbsGrams: line.carbsGrams,
              fatGrams: line.fatGrams,
              origin: line.origin,
            })),
          },
        }),
      );
      return ok(this.mealMapper.transform(dto));
    } catch (cause) {
      return this.mapError(cause);
    }
  }

  async delete(id: SavedMealId): Promise<Result<void, SavedMealError>> {
    try {
      await parseResponse(
        this.rpcClient['saved-meals'][':savedMealId'].$delete({
          param: {savedMealId: id},
        }),
      );
      return ok(undefined);
    } catch (cause) {
      return this.mapError(cause);
    }
  }

  async listRecentLineIngredients(
    input: ListRecentLineIngredientsInput,
  ): Promise<Result<readonly RecentLineIngredient[], SavedMealError>> {
    try {
      const dto = await parseResponse(
        this.rpcClient['recent-items'].$get({
          query: {limit: String(input.limit)},
        }),
      );
      if ('error' in dto) {
        return err(new ServerError());
      }
      return ok(dto.data.map(item => this.recentItemMapper.transform(item)));
    } catch (cause) {
      return this.mapError(cause);
    }
  }

  async clearRecentLineIngredients(): Promise<Result<void, SavedMealError>> {
    try {
      await parseResponse(this.rpcClient['recent-items'].$delete());
      return ok(undefined);
    } catch (cause) {
      return this.mapError(cause);
    }
  }

  async touch(id: SavedMealId): Promise<Result<void, SavedMealError>> {
    try {
      await parseResponse(
        this.rpcClient['saved-meals'][':savedMealId'].touch.$post({
          param: {savedMealId: id},
        }),
      );
      return ok(undefined);
    } catch (cause) {
      return this.mapError(cause);
    }
  }

  async searchLineIngredients(
    input: SearchLineIngredientsInput,
  ): Promise<Result<readonly LineIngredientCandidate[], SavedMealError>> {
    try {
      // Only set filters travel: the server query validator is strict.
      const dto = await parseResponse(
        this.rpcClient['saved-meals']['line-candidates'].$get({
          query: {
            q: input.q,
            ...(input.brand !== null && {brand: input.brand}),
            ...(input.source !== null && {source: input.source}),
          },
        }),
      );
      return this.candidatesFromDto(dto);
    } catch (cause) {
      return this.mapError(cause);
    }
  }

  async lookupLineIngredientByBarcode(
    input: LookupLineIngredientByBarcodeInput,
  ): Promise<Result<readonly LineIngredientCandidate[], SavedMealError>> {
    try {
      const dto = await parseResponse(
        this.rpcClient['saved-meals']['line-candidates'].barcode[':barcode'].$get({
          param: {barcode: input.barcode},
          query: {...(input.source !== null && {source: input.source})},
        }),
      );
      return this.candidatesFromDto(dto);
    } catch (cause) {
      return this.mapError(cause);
    }
  }

  private candidatesFromDto(
    dto: LineCandidatesDto | LineCandidateByBarcodeDto,
  ): Result<readonly LineIngredientCandidate[], SavedMealError> {
    if ('error' in dto) {
      return err(new ServerError());
    }
    const items: LineIngredientCandidate[] = [];
    for (const row of dto.data) {
      const mapped = this.candidateMapper.transform(row);
      if (mapped !== null) {
        items.push(mapped);
      }
    }
    return ok(items);
  }

  private mapError(cause: unknown): Result<never, SavedMealError> {
    if (cause instanceof DetailedError) {
      if (cause.statusCode === 401) {
        return err(new UnauthorizedError());
      }
      if (cause.statusCode === 404) {
        return err(new SavedMealNotFoundError());
      }
      if (cause.statusCode === 400 || cause.statusCode === 422) {
        return err(new InvalidSavedMealInputError());
      }
      return err(new ServerError());
    }
    return err(new NetworkError());
  }
}
